/**
 * @author       matthew@zumper.com (Matthew Talamini)
 * @copyright    Copyright (c) 2022, Zumper
 * @description  helper functions to generate vertical specific detail
 */

import {
  LEASE_LENGTH_SELECTIONS,
  type LEASE_LENGTH_SELECTIONS_TYPE,
  VERTICAL_TYPES,
} from '@zcode-ui/filters'
import type { PetPolicy } from '@zcode/bizos'
import { LeaseType } from '@zcode/enums'
import type { AdditionalBuildingData, Listable } from '@zcode/state-type'

export const getPetFeesInfo = (listable: Listable, petPolicy?: PetPolicy[]) => {
  const description = petPolicy?.find((petPolicy) => !!petPolicy.description)?.description
  if (description) {
    return description
  }

  // fallback if we have no pet policy text
  if (listable?.dogsOk && listable?.catsOk) {
    return 'Both dogs and cats are allowed.'
  }
  if (listable?.dogsOk) {
    return 'Dogs are allowed.'
  }
  if (listable?.catsOk) {
    return 'Cats are allowed.'
  }
  return null
}

export const getLeaseLengthInfo = (listable: Listable) => {
  if (listable.minLeaseDays) {
    return `The minimum lease length is ${listable.minLeaseDays} days.`
  }

  // fallback if we have no lease length info
  if (listable.leaseType) {
    switch (listable.leaseType) {
      case LeaseType.RENTAL:
        return 'This property is for rent.'
      case LeaseType.SUBLET:
        return 'This property is for sublet.'
      case LeaseType.MONTHLY:
        return 'This property is for rent on a monthly basis.'
      case LeaseType.MONTHLY_X3:
        return 'This property is available for a 3-month lease.'
      case LeaseType.MONTHLY_X6:
        return 'This property is available for a 6-month lease.'
      case LeaseType.ANNUAL:
        return 'This property is available for a 1-year lease.'
      case LeaseType.FOR_SALE:
        return 'This property is for sale.'
      default:
        return null
    }
  }
  return null
}

export type LeaseLengths = keyof LEASE_LENGTH_SELECTIONS_TYPE

export interface GetVerticalSpecificDetail {
  leaseLength?: LEASE_LENGTH_SELECTIONS_TYPE[LeaseLengths]
  listable: Listable
  additionalBuildingData?: AdditionalBuildingData
  verticalsArray?: string[]
}

export const getVerticalSpecificDetail = ({
  leaseLength,
  listable,
  verticalsArray,
  additionalBuildingData,
}: GetVerticalSpecificDetail): string | null => {
  if (verticalsArray?.includes(VERTICAL_TYPES.CHEAP)) {
    const rentSpecialInfo = additionalBuildingData?.rentSpecialInfo
    if (rentSpecialInfo?.[0]) {
      return rentSpecialInfo[0]
    }
  }

  if (verticalsArray?.includes(VERTICAL_TYPES.PET_FRIENDLY)) {
    const petFeesInfo = getPetFeesInfo(listable, additionalBuildingData?.petPolicy)
    if (petFeesInfo) {
      return petFeesInfo
    }
  }

  const leaseLengthInfo = getLeaseLengthInfo(listable)
  if (leaseLengthInfo) {
    const isShortTerm = leaseLength === LEASE_LENGTH_SELECTIONS.SHORT_TERM

    if (verticalsArray?.includes(VERTICAL_TYPES.FURNISHED) || isShortTerm) {
      return leaseLengthInfo
    }
  }

  return null
}
